import { MouseEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaDownload, FaExpand, FaMinus, FaPlus } from "react-icons/fa6";
import { IoMdClose } from "react-icons/io";
import { toast } from "react-toastify";
import { getImageDownloadFileName } from "./fileName";
import {
    StyledImageActions,
    StyledImageAttachment,
    StyledImageViewer,
} from "./style";

interface ImageAttachmentProps {
    src: string;
    mimeType: string;
    originalFileName?: string | null;
    storedFileName: string;
}

export const ImageAttachment = ({
    src,
    mimeType,
    originalFileName,
    storedFileName,
}: ImageAttachmentProps) => {
    const [viewerIsOpen, setViewerIsOpen] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [isDownloading, setIsDownloading] = useState(false);
    const downloadFileName = getImageDownloadFileName(originalFileName, storedFileName, mimeType);

    useEffect(() => {
        if (!viewerIsOpen) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setViewerIsOpen(false);
            }
        };

        document.body.style.overflow = "hidden";
        document.addEventListener("keydown", closeOnEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", closeOnEscape);
        };
    }, [viewerIsOpen]);

    const openViewer = (event: MouseEvent) => {
        event.stopPropagation();
        setZoom(1);
        setViewerIsOpen(true);
    };

    const closeViewer = () => {
        setViewerIsOpen(false);
        setZoom(1);
    };

    const downloadImage = async (event: MouseEvent) => {
        event.stopPropagation();

        if (isDownloading) {
            return;
        }

        setIsDownloading(true);

        try {
            const response = await fetch(src);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const blob = await response.blob();
            const resolvedDownloadFileName = getImageDownloadFileName(
                originalFileName,
                storedFileName,
                blob.type || mimeType,
            );
            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement("a");

            anchor.href = objectUrl;
            anchor.download = resolvedDownloadFileName;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
        } catch (error) {
            console.error("Erro ao baixar imagem:", error);
            toast.error("Não foi possível baixar a imagem.");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <StyledImageAttachment onClick={(event) => event.stopPropagation()}>
            <button
                className="image-preview"
                onClick={openViewer}
                title="Ampliar imagem"
                aria-label={`Ampliar imagem ${downloadFileName}`}
            >
                <img src={src} alt={downloadFileName} />
                <span><FaExpand /> Ampliar</span>
            </button>
            <StyledImageActions>
                <button onClick={openViewer} title="Ampliar imagem">
                    <FaExpand /> Ampliar
                </button>
                <button onClick={downloadImage} disabled={isDownloading} title={`Baixar ${downloadFileName}`}>
                    <FaDownload /> {isDownloading ? "Baixando..." : "Baixar"}
                </button>
            </StyledImageActions>

            {viewerIsOpen && createPortal(
                <StyledImageViewer
                    role="dialog"
                    aria-modal="true"
                    aria-label={`Visualização de ${downloadFileName}`}
                    onClick={closeViewer}
                >
                    <header onClick={(event) => event.stopPropagation()}>
                        <p title={downloadFileName}>{downloadFileName}</p>
                        <div>
                            <button
                                onClick={() => setZoom((currentZoom) => Math.max(0.5, currentZoom - 0.25))}
                                disabled={zoom <= 0.5}
                                title="Diminuir zoom"
                                aria-label="Diminuir zoom"
                            >
                                <FaMinus />
                            </button>
                            <span>{Math.round(zoom * 100)}%</span>
                            <button
                                onClick={() => setZoom((currentZoom) => Math.min(4, currentZoom + 0.25))}
                                disabled={zoom >= 4}
                                title="Aumentar zoom"
                                aria-label="Aumentar zoom"
                            >
                                <FaPlus />
                            </button>
                            <button onClick={downloadImage} disabled={isDownloading} title={`Baixar ${downloadFileName}`}>
                                <FaDownload /> <span>{isDownloading ? "Baixando..." : "Baixar"}</span>
                            </button>
                            <button onClick={closeViewer} title="Fechar" aria-label="Fechar visualização">
                                <IoMdClose />
                            </button>
                        </div>
                    </header>
                    <div className="image-area" onClick={(event) => event.stopPropagation()}>
                        <img
                            src={src}
                            alt={downloadFileName}
                            style={{ transform: `scale(${zoom})` }}
                        />
                    </div>
                </StyledImageViewer>,
                document.body,
            )}
        </StyledImageAttachment>
    );
};
